import { Banner, Dialog, LoadingSpinner, Radio, Tooltip, Typography } from '@neo4j-ndl/react';
import { InformationCircleIconOutline } from '@neo4j-ndl/react/icons';
import type {
  Destination,
  LogForwarding,
  LogForwardingIdentity,
  WrappedCloudwatchDestination,
  WrappedLogAnalyticsDestination,
  WrappedStackdriverDestination,
} from '@nx/state';
import { CLOUD_PROVIDER, DESTINATION_TYPE, LOG_TYPE, useInstances } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { ClipboardCopier } from '@nx/ui';
import { CloudProviderIcon } from '@nx/ui/src/cloud-provider-icon/cloud-provider-icon';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import React from 'react';

import { QueryLogFilters } from './create-dialog/query-log-filters';

interface Props<T extends Destination> {
  onClose: () => void;
  open: boolean;
  loading: boolean;
  error: Error | null;
  logForwarding: LogForwarding<T>;
  logForwardingIdentities: LogForwardingIdentity[];
}

const PropertyDisplay = ({
  label,
  text,
  allowCopyText,
}: {
  label: string;
  text?: string | null;
  allowCopyText?: boolean;
}) => {
  return (
    <div className="flex items-center gap-1">
      <Typography variant="label" className="text-neutral-text-weak">
        {label}:
      </Typography>
      <Typography variant="body-medium" className="text-neutral-text-weaker">
        {text ?? '-'}
      </Typography>
      {isNotNullish(allowCopyText) && allowCopyText && isNotNullish(text) && (
        <ClipboardCopier textToCopy={text} ariaLabel="Copy to clipboard" />
      )}
    </div>
  );
};

const CommonViewModal = ({
  children,
  onClose,
  open,
  loading,
  error,
  logForwarding,
  logForwardingIdentities,
}: Props<WrappedStackdriverDestination | WrappedLogAnalyticsDestination | WrappedCloudwatchDestination> & {
  children: ReactNode;
}) => {
  const hasDbId = isNotNullish(logForwarding.db_id) && logForwarding.db_id !== '*' && logForwarding.db_id !== '';
  const { instances, isInstancesLoading } = useInstances({ shouldPoll: false });
  const instance = instances.find((i) => i.id === logForwarding.db_id);

  return (
    <Dialog isOpen={open} onClose={onClose}>
      <Dialog.Header>View configuration</Dialog.Header>
      <Dialog.Content className="space-y-6">
        {error && (
          <Banner type="danger" usage="inline">
            {String(error.message)}
          </Banner>
        )}
        {loading || isInstancesLoading ? (
          <div className="full-width flex justify-center">
            <LoadingSpinner size="large" />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {hasDbId ? (
                <PropertyDisplay label="Instance" text={instance ? instance.name : ''} />
              ) : (
                <PropertyDisplay label="Name" text={logForwarding.name} />
              )}
              {children}
            </div>
            <div className="flex flex-col gap-2">
              <p>Logs to forward:</p>
              {logForwarding.logType === LOG_TYPE.SECURITY && <Radio isChecked label="Security logs" isDisabled />}
              {logForwarding.logType === LOG_TYPE.QUERY && <Radio isChecked label="Query logs" isDisabled />}
            </div>
            {logForwarding.logType === LOG_TYPE.QUERY && isNotNullish(logForwarding.queryLogFilters) && (
              <div>
                <p>Query log filters:</p>
                <QueryLogFilters filterEventType={logForwarding.queryLogFilters.status} readOnly={true} />
              </div>
            )}
          </>
        )}
      </Dialog.Content>
    </Dialog>
  );
};

const CloudProviderNote = () => (
  <Tooltip type="simple" isPortaled={false}>
    <Tooltip.Trigger aria-label="Describe configuration item">
      <InformationCircleIconOutline className={classNames('size-5', 'text-palette-neutral-text-weaker', 'ml-1')} />
    </Tooltip.Trigger>
    <Tooltip.Content className="w-[50%]">
      <Typography variant="body-medium">
        Logs will be forwarded to the log service of the cloud provider which is hosting the instance.
      </Typography>
    </Tooltip.Content>
  </Tooltip>
);

export const ViewStackdriverConfigModal = ({
  onClose,
  open,
  loading,
  error,
  logForwarding,
  logForwardingIdentities,
}: Props<WrappedStackdriverDestination>) => {
  const identity = logForwardingIdentities.find(
    (id) => id.region === logForwarding.region && id.tier === logForwarding.tier,
  );

  return (
    <CommonViewModal
      onClose={onClose}
      open={open}
      loading={loading}
      error={error}
      logForwarding={logForwarding}
      logForwardingIdentities={logForwardingIdentities}
    >
      <div className="flex items-center gap-1">
        <Typography variant="label" className="text-neutral-text-weak">
          Cloud Provider:
        </Typography>
        <CloudProviderIcon cloudProvider={CLOUD_PROVIDER.GCP} />
        <Typography variant="body-medium" className="text-neutral-text-weaker">
          GCP
        </Typography>
        <CloudProviderNote />
      </div>
      <PropertyDisplay
        allowCopyText
        label="Target GCP Project ID"
        text={logForwarding.destination.stackdriver.gcpProjectId}
      />
      <PropertyDisplay
        allowCopyText
        label="Service Account Email"
        text={
          identity?.destinationType === DESTINATION_TYPE.STACKDRIVER && identity.serviceAccountEmail !== ''
            ? identity.serviceAccountEmail
            : 'unknown'
        }
      />
    </CommonViewModal>
  );
};

export const ViewLogAnalyticsConfigModal = ({
  onClose,
  open,
  loading,
  error,
  logForwarding,
  logForwardingIdentities,
}: Props<WrappedLogAnalyticsDestination>) => {
  return (
    <CommonViewModal
      onClose={onClose}
      open={open}
      loading={loading}
      error={error}
      logForwarding={logForwarding}
      logForwardingIdentities={logForwardingIdentities}
    >
      <div className="flex items-center gap-1">
        <Typography variant="label" className="text-neutral-text-weak">
          Cloud Provider:
        </Typography>
        <CloudProviderIcon cloudProvider={CLOUD_PROVIDER.AZURE} />
        <Typography variant="body-medium" className="text-neutral-text-weaker">
          Azure
        </Typography>
        <CloudProviderNote />
      </div>
      <PropertyDisplay
        allowCopyText
        label="Target Workspace ID"
        text={logForwarding.destination.logAnalytics.customerId}
      />
    </CommonViewModal>
  );
};

export const ViewCloudwatchConfigModal = ({
  onClose,
  open,
  loading,
  error,
  logForwarding,
  logForwardingIdentities,
}: Props<WrappedCloudwatchDestination>) => {
  const identity = logForwardingIdentities.find(
    (id) => id.region === logForwarding.region && id.tier === logForwarding.tier,
  );

  return (
    <CommonViewModal
      onClose={onClose}
      open={open}
      loading={loading}
      error={error}
      logForwarding={logForwarding}
      logForwardingIdentities={logForwardingIdentities}
    >
      <div className="flex items-center gap-1">
        <Typography variant="label" className="text-neutral-text-weak">
          Cloud Provider:
        </Typography>
        <CloudProviderIcon cloudProvider={CLOUD_PROVIDER.AWS} />
        <Typography variant="body-medium" className="text-neutral-text-weaker">
          AWS
        </Typography>
        <CloudProviderNote />
      </div>
      <PropertyDisplay label="AWS Region" text={logForwarding.destination.cloudwatch.region} />
      <PropertyDisplay allowCopyText label="Role ARN" text={logForwarding.destination.cloudwatch.logArn} />
      <PropertyDisplay label="Log group name" text={logForwarding.destination.cloudwatch.logGroupName} />
      <PropertyDisplay label="Log stream name" text={logForwarding.destination.cloudwatch.logStreamName} />
      <PropertyDisplay label="Retention days" text={String(logForwarding.destination.cloudwatch.logRetentionDays)} />
      <PropertyDisplay
        allowCopyText
        label="Neo4j ARN"
        text={identity?.destinationType === DESTINATION_TYPE.CLOUDWATCH ? identity.logArn : 'unknown'}
      />
    </CommonViewModal>
  );
};
